import defaults from '@config/defaults'
import { getCoachHitCoordinates, getCoachHitFacility } from '@lib/algolia/coach-geoloc'
import stripMarkdown from '@lib/strip-markdown'
import { composeOptimizedImageUrl } from '@utils/composeOptimizedImageUrl'
import { PGA_COACH_PROGRAMS_SEARCH_PATHNAME } from './constants'
import {
  buildProgramsSeoPathnameFromCurrent,
  getCanonicalCoachSearchUrl,
  isUnscopedProgramsSearchPath,
} from './search-state'

const PRODUCTION_ORIGIN = 'https://www.pga.com'
const COACH_DIRECTORY_PATH = '/coaches/search'
const EVENT_JSON_LD_IMAGE_WIDTH = 1920
const EVENT_JSON_LD_FALLBACK_IMAGE = '/images/Private.jpg'
const GOLF_LESSONS_SHARE_IMAGE_WIDTH = 1200
const PGA_ORGANIZATION_NAME = 'PGA of America'
const PGA_COACH_ORGANIZATION_NAME = 'PGA Coach'

const GOLF_LESSONS_PERSONA_BOOKING_PHRASE = {
  'start-golf': 'beginner golf lessons and programs',
  'play-better': 'game-improvement golf lessons and programs',
  'break-100': 'score-focused golf lessons and programs',
  juniors: 'junior golf lessons and programs',
  'womens-golf': "women's golf lessons and programs",
  'military-veterans': 'military and veterans golf lessons and programs',
}

export const toAbsoluteUrl = (baseUrl, path) => {
  const origin = (baseUrl || PRODUCTION_ORIGIN).replace(/\/$/, '')
  if (!path) return `${origin}/`
  if (/^https?:\/\//.test(path)) return path
  const normalized = path.startsWith('/') ? path : `/${path}`
  return `${origin}${normalized}`
}

const pathnameFromAsPath = (asPath, fallbackPath) => {
  const raw = String(asPath || '').split('?')[0].split('#')[0]
  if (!raw || raw === '/') return fallbackPath
  return raw.startsWith('/') ? raw : `/${raw}`
}

const toIsoDate = (value) => {
  if (!value) return undefined
  const date = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(date.getTime())) return undefined
  return date.toISOString()
}

const stripTitleBrand = (title, suffix) => {
  const pattern = new RegExp(`\\s*\\|\\s*${suffix}$`)
  return String(title || '').replace(pattern, '').trim()
}

const listItem = (position, url, name) => ({
  '@type': 'ListItem',
  position,
  item: {
    '@id': url,
    name,
  },
})

export const getCoachDirectoryDocumentSeo = ({
  placeName = '',
  asPath = COACH_DIRECTORY_PATH,
  baseUrl = PRODUCTION_ORIGIN,
} = {}) => {
  const canonicalUrl = toAbsoluteUrl(baseUrl, pathnameFromAsPath(asPath, COACH_DIRECTORY_PATH))
  const directoryUrl = toAbsoluteUrl(baseUrl, COACH_DIRECTORY_PATH)
  // SSR reverse-geocode can set placeName while asPath is still /coaches/search.
  // Keep title/description aligned with the canonical, not the live location tab.
  const scopedPlaceName = canonicalUrl === directoryUrl ? '' : placeName
  const title = scopedPlaceName
    ? `Find a Golf Coach in ${scopedPlaceName} | PGA of America`
    : 'Find a Golf Coach Near You | PGA of America'
  const description = scopedPlaceName
    ? `Find certified PGA golf coaches in ${scopedPlaceName}. Learn about credentials, specialties, and how they can help you improve.`
    : 'Find certified PGA golf coaches near you. Learn about credentials, specialties, and how they can help you improve.'
  const crumbs = [
    listItem(1, toAbsoluteUrl(baseUrl, '/'), 'PGA Of America'),
    listItem(2, directoryUrl, 'Find a Coach'),
  ]
  if (canonicalUrl !== directoryUrl) {
    crumbs.push(listItem(3, canonicalUrl, placeName || 'Near You'))
  }
  const breadcrumbList = {
    '@type': 'BreadcrumbList',
    itemListElement: crumbs,
  }
  return {
    title,
    description,
    canonicalUrl,
    breadcrumbList,
  }
}

export const buildCoachDirectoryProfessionalServiceJsonLd = (
  hit,
  baseUrl = PRODUCTION_ORIGIN,
  { searchCenter } = {},
) => {
  const origin = (baseUrl || PRODUCTION_ORIGIN).replace(/\/$/, '')
  const coordinates = getCoachHitCoordinates(hit, { searchCenter })
  const facility = getCoachHitFacility(hit, { searchCenter })
  return {
    '@type': 'ProfessionalService',
    name: hit.displayName,
    url: `${origin}/coach/${hit.customSlug}`,
    image: hit.profilePhoto || `${origin}/images/person_icon.png`,
    address: {
      '@type': 'PostalAddress',
      ...(facility?.street && { streetAddress: facility.street }),
      addressLocality: facility?.city || hit.city,
      addressRegion: facility?.state || hit.state,
      postalCode: facility?.zipCode || hit.zipCode,
      addressCountry: 'United States',
    },
    ...(coordinates && {
      areaServed: {
        '@type': 'GeoCircle',
        geoMidpoint: {
          '@type': 'GeoCoordinates',
          latitude: coordinates.lat,
          longitude: coordinates.lng,
        },
        geoRadius: 50,
      },
    }),
  }
}

export const buildGolfLessonsShareImageUrl = (baseUrl = PRODUCTION_ORIGIN) => {
  const origin = (baseUrl || PRODUCTION_ORIGIN).replace(/\/$/, '')
  const absolute = toAbsoluteUrl(origin, defaults.coachJourneyHeroImage)
  return composeOptimizedImageUrl(absolute, GOLF_LESSONS_SHARE_IMAGE_WIDTH) || absolute
}

export const getGolfLessonsDocumentSeo = ({
  personaId = '',
  placeName = '',
  searchState = {},
  asPath,
  baseUrl = PRODUCTION_ORIGIN,
} = {}) => {
  const unscopedDocument = asPath != null && isUnscopedProgramsSearchPath(asPath)
  const seoPlaceName = unscopedDocument ? '' : placeName
  const phrase = GOLF_LESSONS_PERSONA_BOOKING_PHRASE[personaId] || 'golf lessons and programs'
  const title = seoPlaceName
    ? `Book ${phrase} in ${seoPlaceName} | PGA Coach`
    : `Book ${phrase} near you | PGA Coach`
  const description = seoPlaceName
    ? `Find and book private golf lessons, group clinics, and programs with PGA Coaches in ${seoPlaceName}.`
    : 'Find and book private golf lessons, group clinics, and programs with PGA Coaches near you.'
  const origin = (baseUrl || PRODUCTION_ORIGIN).replace(/\/$/, '')
  const baseProgramsUrl = `${origin}${PGA_COACH_PROGRAMS_SEARCH_PATHNAME}`
  const asPathPathname = asPath != null
    ? pathnameFromAsPath(asPath, PGA_COACH_PROGRAMS_SEARCH_PATHNAME)
    : null
  const useAsPathCanonical = Boolean(
    asPathPathname
    && asPathPathname !== PGA_COACH_PROGRAMS_SEARCH_PATHNAME
    && asPathPathname.startsWith(`${PGA_COACH_PROGRAMS_SEARCH_PATHNAME}/`),
  )
  let canonicalUrl = getCanonicalCoachSearchUrl(searchState, baseUrl)
  if (unscopedDocument) {
    canonicalUrl = baseProgramsUrl
  } else if (useAsPathCanonical) {
    canonicalUrl = toAbsoluteUrl(baseUrl, asPathPathname)
  }
  const cityOnlyPathname = useAsPathCanonical
    ? buildProgramsSeoPathnameFromCurrent(asPathPathname, { persona: '' })
    : null
  let cityOnlyUrl = baseProgramsUrl
  if (seoPlaceName && cityOnlyPathname) {
    cityOnlyUrl = toAbsoluteUrl(baseUrl, cityOnlyPathname)
  } else if (seoPlaceName) {
    cityOnlyUrl = getCanonicalCoachSearchUrl({ ...searchState, persona: '' }, baseUrl)
  }
  const currentLabel = stripTitleBrand(title, 'PGA Coach')
  const crumbs = [listItem(1, `${origin}/coach`, 'PGA Coach')]

  if (personaId && seoPlaceName && cityOnlyUrl !== canonicalUrl) {
    crumbs.push(listItem(2, cityOnlyUrl, `Golf lessons and programs in ${seoPlaceName}`))
    crumbs.push(listItem(3, canonicalUrl, currentLabel))
  } else if (seoPlaceName && canonicalUrl !== baseProgramsUrl) {
    crumbs.push(listItem(2, baseProgramsUrl, 'Golf lessons'))
    crumbs.push(listItem(3, canonicalUrl, currentLabel))
  } else {
    crumbs.push(listItem(2, canonicalUrl, currentLabel))
  }

  return {
    title,
    description,
    canonicalUrl,
    unscopedDocument,
    shareImageUrl: buildGolfLessonsShareImageUrl(origin),
    breadcrumbList: {
      '@type': 'BreadcrumbList',
      itemListElement: crumbs,
    },
  }
}

/** National /golf-lessons landing: describe the directory, not IP-geo Event hits. */
export const buildGolfLessonsLandingJsonLd = ({
  title,
  description,
  canonicalUrl,
  image,
  baseUrl = PRODUCTION_ORIGIN,
} = {}) => {
  const origin = (baseUrl || PRODUCTION_ORIGIN).replace(/\/$/, '')
  return {
    '@type': 'CollectionPage',
    name: stripTitleBrand(title, 'PGA Coach') || 'Golf lessons',
    description,
    url: canonicalUrl,
    ...(image ? { image } : {}),
    isPartOf: {
      '@type': 'WebSite',
      name: 'PGA.com',
      url: origin,
    },
    about: {
      '@type': 'Thing',
      name: 'Golf lessons and programs',
    },
    publisher: {
      '@type': 'Organization',
      name: PGA_ORGANIZATION_NAME,
      url: origin,
    },
  }
}

/** Thin/session golf-lessons URLs are noindex; the unscoped landing stays indexable. */
export const shouldNoIndexGolfLessonsDocument = ({
  asPath = PGA_COACH_PROGRAMS_SEARCH_PATHNAME,
  query = '',
  isLoading = false,
  totalHits = 0,
} = {}) => {
  if (String(query || '').trim()) return true
  if (isUnscopedProgramsSearchPath(asPath)) return false
  return !isLoading && totalHits === 0
}

const buildEventLocation = (offering) => {
  if (!offering.venueName && !offering.city && !offering.state) return undefined
  return {
    '@type': 'Place',
    ...(offering.venueName ? { name: offering.venueName } : {}),
    address: {
      '@type': 'PostalAddress',
      ...(offering.city ? { addressLocality: offering.city } : {}),
      ...(offering.state ? { addressRegion: offering.state } : {}),
      addressCountry: 'US',
    },
  }
}

const toPlainText = (value) => {
  if (value == null || value === '') return ''
  return String(stripMarkdown(String(value)) || '').trim()
}

const buildGolfLessonsOfferFields = (offering, url, { includeAvailability = false } = {}) => {
  const hasListedPrice = offering.priceInCents != null && offering.priceInCents !== ''
  const priceInCents = hasListedPrice ? Number(offering.priceInCents) : NaN
  const validFrom = toIsoDate(offering.updatedAt)
  return {
    '@type': 'Offer',
    url,
    ...(includeAvailability ? { availability: 'https://schema.org/InStock' } : {}),
    ...(Number.isFinite(priceInCents) && priceInCents >= 0
      ? { price: priceInCents / 100, priceCurrency: 'USD' }
      : {}),
    ...(validFrom ? { validFrom } : {}),
  }
}

const buildGolfLessonsOfferEntity = (offering, url) => ({
  ...buildGolfLessonsOfferFields(offering, url),
  name: offering.title,
})

const eventFormatNoun = (offering) => (
  offering.format === 'program' ? 'golf program' : 'golf event'
)

const buildEventDescription = (offering) => {
  const fromOffering = toPlainText(offering.description)
  if (fromOffering) return fromOffering
  const noun = eventFormatNoun(offering)
  const coachName = offering.coach?.name
  if (coachName) {
    return `${offering.title} is a PGA Coach ${noun} taught by ${coachName}.`
  }
  return `${offering.title} is a PGA Coach ${noun}.`
}

const isPlaceholderCoachPhoto = (url) => {
  if (!url) return true
  return Boolean(defaults.coachPlaceholder && url === defaults.coachPlaceholder)
}

const buildEventImage = (offering, origin) => {
  const coachPhoto = offering.coach?.photoUrl
  const raw = offering.imageUrl
    || (!isPlaceholderCoachPhoto(coachPhoto) ? coachPhoto : null)
    || EVENT_JSON_LD_FALLBACK_IMAGE
  const absolute = toAbsoluteUrl(origin, raw)
  return composeOptimizedImageUrl(absolute, EVENT_JSON_LD_IMAGE_WIDTH) || absolute
}

const buildEventCoachPerson = (offering, origin) => {
  const name = offering.coach?.name
  if (!name) return null
  const profileUrl = offering.profileUrl || offering.coach?.profileUrl
  return {
    '@type': 'Person',
    name,
    ...(profileUrl ? { url: toAbsoluteUrl(origin, profileUrl) } : {}),
  }
}

const buildGolfLessonsEventEntity = (offering, url, origin) => {
  const endDate = toIsoDate(offering.endDateTime)
  const coachPerson = buildEventCoachPerson(offering, origin)
  return {
    '@type': 'Event',
    name: offering.title,
    url,
    startDate: toIsoDate(offering.startDateTime),
    ...(endDate ? { endDate } : {}),
    location: buildEventLocation(offering),
    description: buildEventDescription(offering),
    image: buildEventImage(offering, origin),
    eventStatus: 'https://schema.org/EventScheduled',
    eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
    offers: buildGolfLessonsOfferFields(offering, url, { includeAvailability: true }),
    performer: coachPerson || {
      '@type': 'PerformingGroup',
      name: PGA_COACH_ORGANIZATION_NAME,
      url: `${origin}/coach`,
    },
    organizer: coachPerson || {
      '@type': 'Organization',
      name: PGA_ORGANIZATION_NAME,
      url: origin,
    },
  }
}

const buildGolfLessonsListItemEntity = (offering, url, origin) => {
  const isGroupOrProgram = offering.format === 'group' || offering.format === 'program'
  const startDate = toIsoDate(offering.startDateTime)
  const location = buildEventLocation(offering)
  if (isGroupOrProgram && startDate && location) {
    return buildGolfLessonsEventEntity(offering, url, origin)
  }
  return buildGolfLessonsOfferEntity(offering, url)
}

export const buildGolfLessonsResultJsonLd = (offerings = [], baseUrl = PRODUCTION_ORIGIN) => {
  const origin = (baseUrl || PRODUCTION_ORIGIN).replace(/\/$/, '')
  const itemListElement = []
  offerings.forEach((offering) => {
    if (!offering?.ctaUrl) return
    const url = toAbsoluteUrl(origin, offering.ctaUrl)
    itemListElement.push({
      '@type': 'ListItem',
      position: itemListElement.length + 1,
      item: buildGolfLessonsListItemEntity(offering, url, origin),
    })
  })
  return {
    '@type': 'ItemList',
    itemListElement,
  }
}
